import { useEffect, useId, useRef, type ReactNode } from 'react';

import estilos from './Gaveta.module.css';

/**
 * Painel lateral modal.
 *
 * POR QUE `<dialog>` NATIVO, E NAO UMA `<div>` COM POSITION FIXED
 *
 * `showModal()` entrega de graca o que uma div exigiria reimplementar e quase sempre sai
 * errado: foco preso dentro do painel, `Esc` fechando, o resto da pagina inerte para
 * teclado e leitor de tela, e a camada acima de tudo sem disputa de z-index.
 *
 * O QUE FICA POR NOSSA CONTA
 *
 * 1. `Esc` dispara o evento `cancel`, e nao o nosso fechamento. Sem interceptar, o
 *    navegador fecharia o dialogo e a URL continuaria em /acoes/nova — a gaveta
 *    desapareceria da tela mas seguiria "aberta" para o roteador, e um F5 a traria de
 *    volta.
 * 2. Clique no fundo. O `::backdrop` nao recebe clique; o que chega e um clique no
 *    proprio `<dialog>` fora do conteudo, e e assim que se detecta.
 */

interface Props {
  titulo: string;
  /** Linha de apoio sob o titulo. Vira a descricao acessivel do dialogo. */
  descricao?: string;
  /**
   * `media` para formulario, `larga` para leitura em grade.
   *
   * Duas opcoes, e nao um numero: largura livre por chamada produziria uma gaveta
   * diferente em cada tela sem ninguem decidir isso.
   */
  largura?: 'media' | 'larga';
  /** Chamado por Esc, pelo X e pelo clique no fundo. */
  aoFechar: () => void;
  children: ReactNode;
}

export function Gaveta({ titulo, descricao, largura = 'media', aoFechar, children }: Props) {
  const referencia = useRef<HTMLDialogElement>(null);
  const idDoTitulo = useId();
  const idDaDescricao = useId();

  useEffect(() => {
    const dialogo = referencia.current;

    if (dialogo === null) {
      return;
    }

    dialogo.showModal();

    /*
      A pagina atras nao rola enquanto a gaveta esta aberta. O `<dialog>` modal ja
      impede a interacao, mas nao o scroll do documento, e rolar o fundo com o painel
      parado por cima e desorientador.
    */
    const rolagemAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = rolagemAnterior;

      if (dialogo.open) {
        dialogo.close();
      }
    };
  }, []);

  return (
    <dialog
      ref={referencia}
      className={largura === 'larga' ? `${estilos.gaveta} ${estilos.larga}` : estilos.gaveta}
      aria-labelledby={idDoTitulo}
      {...(descricao === undefined ? {} : { 'aria-describedby': idDaDescricao })}
      onCancel={(evento) => {
        // Quem decide fechar e quem abriu, porque fechar aqui significa tambem sair da
        // rota. Ver o item 1 do comentario acima.
        evento.preventDefault();
        aoFechar();
      }}
      onClick={(evento) => {
        if (evento.target === referencia.current) {
          aoFechar();
        }
      }}
    >
      <div className={estilos.conteudo}>
        <header className={estilos.cabecalho}>
          <div>
            <h2 className={estilos.titulo} id={idDoTitulo}>
              {titulo}
            </h2>
            {descricao !== undefined && (
              <p className={estilos.descricao} id={idDaDescricao}>
                {descricao}
              </p>
            )}
          </div>

          <button
            type="button"
            className={estilos.fechar}
            onClick={aoFechar}
            aria-label="Fechar"
            title="Fechar"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className={estilos.corpo}>{children}</div>
      </div>
    </dialog>
  );
}
